import { Box, Dialog, DialogContent, Theme, useTheme } from '@mui/material';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import React from 'react';
import { EMBEDDED_CONTENT_PROPS } from '../../../lib/app/embedded-content';

interface YoutubeVideoProps {
  video_id: string;
  height?: number | string;
  width?: number | string;
  thumbnail?: string;
  play_in_modal?: boolean;
}

/**
 * Reusable embedded Youtube Video player.
 * @param {string} video_id ID of youtube video from URL to embed.
 * @param {number | string | undefined} height Optionally provide custom height for iframe. Defaults to full height of parent.
 * @param {number | string | undefined} width Optionally provide custom width for iframe. Defaults to full width of parent.
 * @param {string | undefined} thumbnail Optionally provide a custom thumbnail image, will default to Youtube video thumbnail.
 * @param {boolean | undefined} play_in_modal Optionally play the Youtube video in a larger view popup modal.
 * @returns {JSX.Element}
 */
const YoutubeVideo = ({ video_id, height, width, thumbnail, play_in_modal }: YoutubeVideoProps) => {
  const [displayPlayModal, setDisplayPlayModal] = React.useState<boolean>(false);
  const theme: Theme = useTheme();

  const handleCloseDisplayModal = (): void => {
    setDisplayPlayModal(false);
  };

  return (
    <>
      {/* Modal for larger video play area. */}
      <Dialog open={displayPlayModal} onClose={handleCloseDisplayModal} maxWidth="xl">
        <DialogContent
          sx={{
            px: 0,
            py: 0
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '80vw',
              maxWidth: '960px',
              mx: 'auto',
              height: 0,
              paddingBottom: '56.25%' // 16:9 aspect ratio
            }}
          >
            <iframe
              src={`https://www.youtube.com/embed/${video_id}`}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              frameBorder="0"
              {...EMBEDDED_CONTENT_PROPS}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%'
              }}
            ></iframe>
          </Box>
        </DialogContent>
      </Dialog>

      <Box>
        {play_in_modal ? (
          // Display video preview as image with modal toggling onClick and play button overlay.
          <Box
            onClick={() => setDisplayPlayModal(true)}
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <img
              src={thumbnail ? thumbnail : `https://img.youtube.com/vi/${video_id}/hqdefault.jpg`}
              alt="Open Video"
              draggable={false}
              style={{
                position: 'absolute',
                left: 0,
                width: '100%',
                height: '100%',
                borderRadius: '12px',
                objectFit: 'cover'
              }}
            />

            {/* Play button overlay */}
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                backgroundColor: theme.palette.tint.main,
                boxShadow: `0px 0px 8px 3px rgba(0,0,0,0.25)`
              }}
            >
              <PlayCircleIcon
                sx={{
                  fontSize: 64,
                  color: 'white'
                }}
              />
            </Box>
          </Box>
        ) : (
          // Display standard Youtube video embed in container.
          <iframe
            src={`https://www.youtube.com/embed/${video_id}`}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            frameBorder="0"
            {...EMBEDDED_CONTENT_PROPS}
            style={{
              position: 'absolute',
              width: width ?? '100%',
              height: height ?? '100%',
              borderRadius: '12px'
            }}
          ></iframe>
        )}
      </Box>
    </>
  );
};

export default YoutubeVideo;
